/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { userAPI } from '../../services/apiService';
import './UsersList.css';

const convertPersianToEnglishDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
};

const convertEnglishToPersianDigits = (str) => {
    if (str === null || str === undefined) return '';
    return String(str).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};

function Users() {
    const { t, i18n } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const group = localStorage.getItem('userGroup');

    useEffect(() => {
        const fetchUsers = async () => {
            if (!token || !group) {
                setError(t('auth_or_group_missing'));
                setLoading(false);
                return;
            }

            try {
                const data = await userAPI.getMembers(group, { fallback: [] });

                if (data?.detail === 'You cannot access this view unless you be admin user.') {
                    throw new Error(t('admin_access_required'));
                }
                setUsers(data || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [token, group, t]);

    const handleEdit = (user) => {
        console.log('Edit user:', user);
    };

    const handleDelete = (user) => {
        console.log('Delete user:', user);
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };
    const filteredUsers = users.filter(
        (user) =>
            (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="users_page">
            <Sidebar />
            <div className="users_main">
                <Navbar />
                <div className="users_content">
                    <div className="users_header">
                        <Button variant="contained" className="add_btn">
                            {t('addingNewUser')}
                        </Button>
                        <div className="search_bar">
                            <input
                                type="text"
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="user_search_input"
                            />
                        </div>
                    </div>

                    {loading && (
                        <div style={{ textAlign: 'center', marginTop: 40 }}>
                            <CircularProgress />
                            <p>{t('loading')}</p>
                        </div>
                    )}
                    {error && (
                        <p style={{ color: 'red' }}>
                            {t('error')}: {error}
                        </p>
                    )}

                    {!loading && !error && (
                        <TableContainer component={Paper} className="categories_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('name')}</TableCell>
                                        <TableCell>{t('lastname')}</TableCell>
                                        <TableCell>{t('phone')}</TableCell>
                                        <TableCell>{t('email')}</TableCell>
                                        <TableCell>{t('manager')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell data-label={t('name')}>
                                                {user.first_name || '-'}
                                            </TableCell>
                                            <TableCell data-label={t('lastname')}>
                                                {user.last_name || '-'}
                                            </TableCell>
                                            <TableCell data-label={t('phone')}>
                                                {i18n.language === 'fa'
                                                    ? convertEnglishToPersianDigits(
                                                          user.phone_number || '-'
                                                      )
                                                    : convertPersianToEnglishDigits(
                                                          user.phone_number || '-'
                                                      )}
                                            </TableCell>
                                            <TableCell data-label={t('email')}>
                                                {user.email || '-'}
                                            </TableCell>
                                            <TableCell data-label={t('manager')}>
                                                <div
                                                    className="user-status"
                                                    style={{
                                                        backgroundColor: user.is_staff
                                                            ? 'green'
                                                            : 'red',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell data-label="">
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleDelete(user)}
                                                    sx={{ marginLeft: '10px' }}
                                                >
                                                    {t('delete')}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleEdit(user)}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Users;
